import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/hooks/use-language';
import { useTournament } from '@/hooks/use-tournament';
import { AmericanoTournament } from '@/components/tournament/AmericanoTournament';

export default function AmericanoTournamentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { startTournament, resetTournament, tournamentState } = useTournament();
  const [participants, setParticipants] = useState<any[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Process participant data once when component mounts
  useEffect(() => {
    console.log('AmericanoTournamentPage - initializing with location.state:', location.state);
    
    let tournamentParticipants: any[] = [];
    
    // Process participant data
    if (location.state?.participantDetails) {
      console.log('Processing participant details:', location.state.participantDetails);
      
      tournamentParticipants = location.state.participantDetails.map((participant: any, index: number) => {
        const [fornavn, ...etternavnParts] = participant.name.split(' ');
        const etternavn = etternavnParts.join(' ');
        
        return {
          medlemsid: participant.id || (index + 1).toString(),
          fornavn: fornavn || 'Unknown',
          etternavn: etternavn || 'Player',
          mobil: participant.phone || `+47 ${Math.floor(Math.random() * 90000000) + 10000000}`,
          epost: `${fornavn?.toLowerCase() || 'player'}@example.com`,
          bekreftelsestidspunkt: participant.joinedAt ? new Date(participant.joinedAt).toISOString() : new Date().toISOString(),
          joinedAt: participant.joinedAt ? new Date(participant.joinedAt) : undefined
        };
      });
    } else if (location.state?.participants) {
      console.log('Processing participant names:', location.state.participants);
      
      tournamentParticipants = location.state.participants.map((name: string, index: number) => {
        const [fornavn, ...etternavnParts] = name.split(' ');
        const etternavn = etternavnParts.join(' ');
        
        return {
          medlemsid: (index + 1).toString(),
          fornavn: fornavn || 'Unknown',
          etternavn: etternavn || 'Player',
          mobil: `+47 ${Math.floor(Math.random() * 90000000) + 10000000}`,
          epost: `${fornavn?.toLowerCase() || 'player'}@example.com`,
          bekreftelsestidspunkt: new Date().toISOString()
        };
      });
    } else {
      // Fallback: check if there's already an active Americano tournament
      console.log('No participant data in location.state, checking existing tournament state');
      if (tournamentState.isActive && tournamentState.tournamentType === 'americano' && tournamentState.americanoPlayers?.length > 0) {
        console.log('Found existing Americano tournament, using existing players');
        // Convert existing players to participant format for consistency
        tournamentParticipants = tournamentState.americanoPlayers.map((player, index) => ({
          medlemsid: player.id || (index + 1).toString(),
          fornavn: player.name.split(' ')[0] || 'Player',
          etternavn: player.name.split(' ').slice(1).join(' ') || (index + 1).toString(),
          mobil: `+47 ${Math.floor(Math.random() * 90000000) + 10000000}`,
          epost: `player${index + 1}@example.com`,
          bekreftelsestidspunkt: new Date().toISOString()
        }));
        setHasStarted(true); // Tournament is already started
      }
    }
    
    if (tournamentParticipants.length > 0) {
      // Ensure unique player IDs and names to avoid duplicates
      const seenIds = new Set<string>();
      const seenNames = new Set<string>();
      
      tournamentParticipants = tournamentParticipants.map((p, idx) => {
        // Ensure unique ID
        let uniqueId = p.medlemsid;
        if (seenIds.has(uniqueId)) {
          uniqueId = `${uniqueId}-${idx}`;
          console.warn(`Duplicate ID detected: ${p.medlemsid}, changed to ${uniqueId}`);
        }
        seenIds.add(uniqueId);
        
        // Ensure unique name display
        const fullName = `${p.fornavn} ${p.etternavn || ''}`.trim();
        let uniqueName = fullName;
        let counter = 2;
        while (seenNames.has(uniqueName)) {
          uniqueName = `${fullName} (${counter})`;
          counter++;
        }
        seenNames.add(uniqueName);
        
        return {
          ...p,
          medlemsid: uniqueId,
          displayName: uniqueName !== fullName ? uniqueName : undefined
        };
      });
      
      setParticipants(tournamentParticipants);
      setIsReady(true);
    } else {
      // Only redirect if there's ALSO no active Americano tournament
      // This prevents redirecting after tournament starts and component re-renders
      if (!(tournamentState.isActive && tournamentState.tournamentType === 'americano')) {
        console.warn('No participants found and no active tournament, redirecting to dashboard');
        navigate('/');
      } else {
        console.log('Tournament is active, staying on page');
        setIsReady(true);
        setHasStarted(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle tournament reset and start
  const initializeTournament = useCallback(async () => {
    if (!isReady || participants.length === 0 || hasStarted) {
      return;
    }

    console.log('🎮 AmericanoTournamentPage: Initializing tournament with participants:', 
      participants.map(p => ({
        name: `${p.fornavn} ${p.etternavn}`,
        joinedAt: p.joinedAt
      }))
    );
    console.log('Initializing tournament...');
    
    try {
      // Reset tournament if it's active
      if (tournamentState.isActive || tournamentState.currentRound > 0) {
        console.log('Resetting existing tournament');
        // Silent reset to avoid showing a reset toast at round 1
        resetTournament(true);
        
        // Wait a bit for reset to complete
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Convert participants to Player format for tournament
      // Use medlemsid as the stable unique ID (ensured unique earlier)
      const players = participants.map((p, index) => ({
        id: p.medlemsid || p.mobil || String(index + 1),
        // Use displayName if available (for handling duplicates), otherwise construct full name
        name: p.displayName || (p.etternavn ? `${p.fornavn} ${p.etternavn}` : p.fornavn || `Player ${index + 1}`),
        phone: p.mobil,
        points: 0,
        matchesPlayed: 0,
        joinedAt: p.joinedAt
      }));

      console.log('🎯 AmericanoTournamentPage: Players array with joinedAt:', 
        players.map(p => ({ name: p.name, joinedAt: p.joinedAt }))
      );

      const selectedType: 'americano' | 'team_americano' = (location.state?.tournamentType === 'team_americano') ? 'team_americano' : 'americano';
      if (players.length >= 4) {
        // No upper limit - bench extras each round to nearest multiple-of-4 automatically
        console.log(`Starting ${selectedType} tournament with ${players.length} players:`, players);
        setHasStarted(true);
        
        // Small delay to ensure state is updated
        setTimeout(() => {
          startTournament(players, 0, selectedType);
        }, 100);
      } else {
        console.warn('Not enough players to start tournament:', players.length);
        navigate('/');
      }
    } catch (error) {
      console.error('Error initializing tournament:', error);
      navigate('/');
    }
  }, [isReady, participants, hasStarted, tournamentState.isActive, tournamentState.currentRound, resetTournament, startTournament, navigate, location.state?.tournamentType]);

  // Initialize tournament when ready
  useEffect(() => {
    if (isReady && !hasStarted) {
      const timer = setTimeout(() => {
        initializeTournament();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isReady, hasStarted, initializeTournament]);

  const handleBack = () => {
    console.log('=== BACK BUTTON CLICKED ===');
    console.log('Current location.state:', location.state);
    console.log('fromTournamentId:', location.state?.fromTournamentId);
    
    // Always reset tournament state when leaving
    console.log('Resetting tournament state...');
    resetTournament();
    
    // Go back to the specific tournament detail page if we came from there
    if (location.state?.fromTournamentId) {
      const tournamentId = location.state.fromTournamentId;
      console.log('Navigating back to tournament:', tournamentId);
      const targetUrl = `/tournament/${tournamentId}`;
      console.log('Navigation URL:', targetUrl);
      navigate(targetUrl, { replace: true });
      console.log('Navigate function called with replace: true');
    } else if (location.state?.fromEventId) {
      const eventId = location.state.fromEventId;
      console.log('Navigating back to event:', eventId);
      navigate(`/event/${eventId}`, { replace: true });
    } else {
      // Otherwise go back to dashboard
      console.log('No source id found, going to dashboard');
      navigate('/', { replace: true });
    }
  };

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Don't reset tournament on unmount if it's active and running
      // Let the user continue the tournament from other pages
      console.log('AmericanoTournamentPage unmounting');
    };
  }, []);

  return (
    <div className="space-y-6 max-w-full min-w-0">
      {/* Header with back button - only show when tournament is not active */}
      {!tournamentState.isActive && (
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("nav.back")}
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              {location.state?.tournamentType === 'team_americano' ? 'Team Americano Tournament' : 'Americano Tournament'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {participants.length > 0 && `Playing with ${participants.length} participants`}
            </p>
          </div>
        </div>
      )}

      {/* Tournament component */}
      {(tournamentState.tournamentType === 'americano' || tournamentState.tournamentType === 'team_americano') && (tournamentState.isActive || tournamentState.isFinished) ? (
        <AmericanoTournament participants={participants} onBack={handleBack} />
      ) : !hasStarted ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">{t("tournament.preparing")}</p>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">{t("tournament.starting")}</p>
        </div>
      )}

    </div>
  );
}
